/**
 * /workshop — user-authored, KB-driven analysis pages.
 *
 * Loads the team's KB list (so the "create page" dialog can offer a
 * scope selector), then hands everything to the browser.
 *
 * Workshop pages are persisted in Postgres via ``/v1/workshop/pages``
 * (team-scoped). Analysis fills ``source_prompt`` + ``last_analyze_source``
 * so 「重新分析」 can refresh from live KB data.
 */

import { useLoaderData } from "react-router-dom";

import { PageHeader } from "@/components/page-header";
import { WorkshopBrowser } from "@/features/workshop/workshop-browser";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import type { KbView } from "@/lib/types";

import { useAuthState } from "../lib/auth";

interface WorkshopLoaderData {
  kbs: KbView[];
}

export async function workshopLoader(): Promise<WorkshopLoaderData> {
  // Empty list on failure is fine: the browser still renders, the
  // create-page dialog just won't have a KB to bind to.
  const kbs = await api<KbView[]>("/kbs").catch(() => [] as KbView[]);
  return { kbs };
}

export default function WorkshopRoute() {
  const { kbs } = useLoaderData() as WorkshopLoaderData;
  const auth = useAuthState();
  const t = useT();
  return (
    <section className="space-y-4">
      <PageHeader
        eyebrow="workspace"
        title={t("workshop.title")}
        subtitle={t("workshop.subtitle")}
      />
      <WorkshopBrowser teamId={auth.tenant.team_id} kbs={kbs} />
    </section>
  );
}
